"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

function TooltipProvider({ delayDuration = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
    return <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />
}

/** Prefer wrapping the app (or a subtree) with {@link TooltipProvider} once — each Tooltip is only the Radix Root. */
function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
    return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
    return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
    className,
    sideOffset = 0,
    collisionPadding = 8,
    children,
    hideArrow = false,
    ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & { hideArrow?: boolean }) {
    // Project-wide 8px viewport buffer — matches popover.tsx + dropdown-menu.tsx.
    return (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
                data-slot="tooltip-content"
                sideOffset={sideOffset}
                collisionPadding={collisionPadding}
                className={cn(
                    // `py-2` (was `py-1.5`) gives multi-line tooltips visible
                    // top/bottom breathing room — the prior `py-1.5` looked
                    // top-hugging once content wrapped, since the glyph cap-
                    // height sits high in its line box and tight padding
                    // exaggerates the imbalance. `[text-wrap:pretty]` replaces
                    // the prior `text-balance`, which was forcing shorter
                    // balanced lines than needed and leaving a dead strip of
                    // background on the right edge of the (`w-fit`-shrunk)
                    // tooltip.
                    "bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[100] w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-2.5 py-2 text-xs [text-wrap:pretty]",
                    className
                )}
                {...props}
            >
                {children}
                {hideArrow ? null : (
                    <TooltipPrimitive.Arrow
                        // Native Radix Arrow renders an SVG <polygon> triangle.
                        // Using width/height + fill keeps the arrow flush with
                        // the tooltip body — no translate gap, no rotated
                        // square (which read as a separate diamond).
                        width={10}
                        height={5}
                        className="fill-foreground"
                    />
                )}
            </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
    )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
